import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import ClearIcon from '@material-ui/icons/Clear';

import { translate } from 'services/i18nService';


import ACHIEVEMENT_IMAGES from '../../../../images/achievements';
import {
  capitalizeDescription,
  normalizeAchievementsStrings,
  normalizeInformation,
} from '../Utils';

import ProgressBar from './ProgressBar';
import styles from './AchievementsModal.css';


function normalizeTitle(completion) {
  if (completion) {
    return translate('gamification.trophies.modaltitle.titlelocked');
  }

  return translate('gamification.trophies.modaltitle.titleunlocked');
}

function normalizeProgress(achievementProgress, achievementCompletion) {
  const isUnlocked = achievementCompletion !== null;
  const progress = achievementProgress || 0;

  if (isUnlocked || progress >= 100) {
    const text = translate('gamification.trophies.trophycard.unlockedtext');
    const date = new Date(achievementCompletion).toLocaleDateString();

    return `${text} ${date}`;
  }

  const text = translate('gamification.trophies.trophycard.completedtext');
  const percentage = `${progress}%`;

  return `${percentage} ${text}`;
}

function AchievementsModal(props) {
  const { achievement, open, performerName, onClose, onNextPage, onPreviousPage } = props;

  const { completion, progress, rate, name, aditionalData } = achievement;
  const isLocked = completion === null;
  const refBox = useRef(null);
  const onBoxClose = useCallback((e) => refBox.current === e.target && onClose(), [onClose]);

  if (!open) {
    return null;
  }

  const infoText = capitalizeDescription(
    normalizeInformation(
      normalizeAchievementsStrings(name, 'information'),
      performerName
    ),
    aditionalData,
    performerName
  );

  const descriptionType = isLocked ? 'descriptionlocked' : 'descriptionunlocked';
  const description = capitalizeDescription(
    translate(normalizeAchievementsStrings(name, descriptionType)),
    aditionalData,
    performerName
  );

  return (
    <Modal data-cy="modal_trophies" disableScrollLock={true} open={open} onClose={onClose}>
      <div ref={refBox} className={styles.modal} onClick={onBoxClose}>
        <ArrowBackIosIcon
          className={styles.arrow}
          color="action"
          data-cy="modal_prev_button"
          fontSize="large"
          onClick={onPreviousPage}
        />
        <div className={styles.contain} data-cy="modal_container">
          <div className={styles.title} data-cy="modal_top_title">
            {normalizeTitle(completion)}
          </div>

          <div className={styles.wrapper}>
            <div className={isLocked ? styles.backgroundLocked : styles.background}>
              <img
                alt=""
                className={isLocked ? styles.lockedImage : styles.image}
                data-cy={isLocked ? 'modal_image_locked' : 'modal_image_unlocked'}
                src={ACHIEVEMENT_IMAGES[name?.toLowerCase()]}
              />
            </div>

            <div className={styles.content}>
              <p className={styles.achievementName} data-cy="modal_title">
                {translate(normalizeAchievementsStrings(name, 'title')).toUpperCase()}
              </p>

              <p
                className={styles.trophiesDescriptionTitle}
                data-cy="modal_information"
              >
                {infoText}
              </p>

              <p className={styles.description} data-cy="modal_description">
                {description}
              </p>

              <p className={styles.data} data-cy="modal_percent">
                {normalizeProgress(progress, completion)}
              </p>

              {isLocked && (
                <ProgressBar
                  progress={progress}
                  type="modal"
                />
              )}

              <p className={styles.percent} data-cy="modal_ratio">
                {rate === null ? 0 : rate}%{' '}
                {translate('gamification.trophies.trophycard.subtitlepercent')}
              </p>
            </div>
          </div>
        </div>
        <ArrowForwardIosIcon
          className={styles.arrow}
          color="action"
          data-cy="modal_next_button"
          fontSize="large"
          onClick={onNextPage}
        />

        <ClearIcon
          className={styles.cross}
          color="action"
          data-cy="modal_close_button"
          fontSize="large"
          onClick={onClose}
        />
      </div>
    </Modal>
  );
}

AchievementsModal.propTypes = {
  achievement: PropTypes.shape({
    aditionalData: PropTypes.string,
    completion: PropTypes.number,
    name: PropTypes.string,
    rate: PropTypes.number,
    progress: PropTypes.number,
  }).isRequired,
  performerName: PropTypes.string.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onNextPage: PropTypes.func,
  onPreviousPage: PropTypes.func,
};

AchievementsModal.defaultProps = {
  achievement: {
    aditionalData: '',
    completion: null,
    name: '',
    rate: null,
    progress: null,
  },
  open: false,
  onNextPage: () => undefined,
  onPreviousPage: () => undefined,
  onClose: () => undefined,
};

export default AchievementsModal;
